import { useCallback, useMemo, useReducer } from 'react';

const initialState = {
  items: [],
  globalDiscount: null,
  payments: [],
  notes: '',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existing = state.items.find((item) => item.product_id === action.payload.product_id);
      if (existing) {
        return {
          ...state,
          items: state.items.map((item) =>
            item.product_id === action.payload.product_id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          ),
        };
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload, quantity: 1 }],
      };
    }
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map((item) =>
          item.product_id === action.payload.product_id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter((item) => item.product_id !== action.payload.product_id),
      };
    case 'SET_LINE_DISCOUNT':
      return {
        ...state,
        items: state.items.map((item) =>
          item.product_id === action.payload.product_id
            ? { ...item, discount: action.payload.discount }
            : item
        ),
      };
    case 'SET_GLOBAL_DISCOUNT':
      return { ...state, globalDiscount: action.payload };
    case 'CLEAR_CART':
      return { ...state, items: [], globalDiscount: null, payments: [] };
    case 'ADD_PAYMENT':
      return { ...state, payments: [...state.payments, action.payload] };
    case 'REMOVE_PAYMENT':
      return {
        ...state,
        payments: state.payments.filter((payment) => payment.id !== action.payload.id),
      };
    case 'SET_NOTES':
      return { ...state, notes: action.payload };
    default:
      return state;
  }
};

const calculateLineDiscount = (line) => {
  if (!line?.discount) {
    return 0;
  }
  const base = line.unit_price * line.quantity;
  if (line.discount.type === 'percent') {
    return base * (line.discount.value / 100);
  }
  return line.discount.value;
};

const calculateGlobalDiscount = (subtotal, discount) => {
  if (!discount) {
    return 0;
  }
  if (discount.type === 'percent') {
    return subtotal * (discount.value / 100);
  }
  return discount.value;
};

export const usePosState = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const totals = useMemo(() => {
    const subtotal = state.items.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);
    const lineDiscounts = state.items.reduce((sum, item) => sum + calculateLineDiscount(item), 0);
    const subtotalAfterLineDiscounts = Math.max(subtotal - lineDiscounts, 0);
    const globalDiscount = calculateGlobalDiscount(subtotalAfterLineDiscounts, state.globalDiscount);
    const total = Math.max(subtotalAfterLineDiscounts - globalDiscount, 0);
    const paid = state.payments.reduce((sum, payment) => sum + payment.amount, 0);
    const remaining = Math.max(total - paid, 0);
    return {
      subtotal,
      lineDiscounts,
      globalDiscount,
      total,
      paid,
      remaining,
    };
  }, [state.items, state.globalDiscount, state.payments]);

  const addItem = useCallback((payload) => dispatch({ type: 'ADD_ITEM', payload }), []);
  const updateQuantity = useCallback((payload) => dispatch({ type: 'UPDATE_QUANTITY', payload }), []);
  const removeItem = useCallback((payload) => dispatch({ type: 'REMOVE_ITEM', payload }), []);
  const setLineDiscount = useCallback((payload) => dispatch({ type: 'SET_LINE_DISCOUNT', payload }), []);
  const setGlobalDiscount = useCallback((payload) => dispatch({ type: 'SET_GLOBAL_DISCOUNT', payload }), []);
  const clearCart = useCallback(() => dispatch({ type: 'CLEAR_CART' }), []);
  const addPayment = useCallback((payload) => dispatch({ type: 'ADD_PAYMENT', payload }), []);
  const removePayment = useCallback((payload) => dispatch({ type: 'REMOVE_PAYMENT', payload }), []);
  const setNotes = useCallback((payload) => dispatch({ type: 'SET_NOTES', payload }), []);

  return {
    state,
    totals,
    addItem,
    updateQuantity,
    removeItem,
    setLineDiscount,
    setGlobalDiscount,
    clearCart,
    addPayment,
    removePayment,
    setNotes,
  };
};
